<script setup lang="ts">
// Mobile-only target picker: the fast lane + each agent as a compact dropdown
// instead of a wrapping row of pills (which eats 2-3 rows of vertical space on
// a phone). Shared by the QuickAsk overlay and the HomeView composer; the
// desktop pill rows in those views stay as-is and hide below --bp-sm, where
// this takes over. Selecting an item relays to the parent via `pick` / `more`.
import { computed } from 'vue';
import AppIcon from '@/components/AppIcon.vue';
import Avatar from '@/components/Avatar.vue';
import { Menu, MenuTrigger, MenuContent, MenuItem, MenuSeparator } from '@hq/chat-ui';
import { useI18n } from '@/lib/i18n';
import type { LaunchAgent } from '@/stores/launch';

const props = defineProps<{
  target: 'fast' | string;
  agents: LaunchAgent[];
  fastDisabled?: boolean;
  hasMore?: boolean;
}>();
const emit = defineEmits<{ (e: 'pick', target: 'fast' | string): void; (e: 'more'): void }>();

const { t } = useI18n();

const selectedAgent = computed<LaunchAgent | null>(() =>
  props.target === 'fast' ? null : props.agents.find((a) => a.id === props.target) ?? null,
);
</script>

<template>
  <div class="tmenu">
    <Menu>
      <MenuTrigger>
        <button type="button" class="tmenu__trigger" :aria-label="t('quickask.send_to')">
          <template v-if="selectedAgent">
            <Avatar :name="selectedAgent.display_name" :src="selectedAgent.avatar_url" size="xs" />
            <span class="tmenu__label">{{ selectedAgent.display_name }}</span>
          </template>
          <template v-else>
            <AppIcon name="lightning" :size="14" />
            <span class="tmenu__label">{{ t('quickask.quick_ask') }}</span>
          </template>
          <AppIcon name="caret-down" :size="12" class="tmenu__caret" />
        </button>
      </MenuTrigger>
      <!-- Opens UPWARD: the picker sits just above the bottom composer. -->
      <MenuContent side="top" align="start" :label="t('quickask.send_to')" min-width="220px">
        <MenuItem :disabled="fastDisabled" @select="emit('pick', 'fast')">
          <AppIcon name="lightning" :size="16" />
          <span>{{ t('quickask.quick_ask') }}</span>
          <AppIcon v-if="target === 'fast'" name="check" :size="14" class="tmenu__check" />
        </MenuItem>
        <MenuSeparator />
        <MenuItem v-for="a in agents" :key="a.id" @select="emit('pick', a.id)">
          <Avatar :name="a.display_name" :src="a.avatar_url" size="xs" />
          <span>{{ a.display_name }}</span>
          <AppIcon v-if="target === a.id" name="check" :size="14" class="tmenu__check" />
        </MenuItem>
        <template v-if="hasMore">
          <MenuSeparator />
          <MenuItem @select="emit('more')">
            <AppIcon name="dots-three" :size="16" />
            <span>{{ t('quickask.all_agents') }}</span>
          </MenuItem>
        </template>
      </MenuContent>
    </Menu>
  </div>
</template>

<style scoped lang="scss">
// Hidden on desktop (the pill rows in the host views handle that); shown below
// --bp-sm where the host hides its pills.
.tmenu { display: none; }
@media (max-width: 479.98px) {
  // Sits where the pill row was (the host hides its pills here), with matching
  // padding so it lines up above the composer.
  .tmenu { display: block; padding: var(--space-3) var(--space-3) 0; }
}
.tmenu__trigger {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  max-width: 100%;
  padding: 5px var(--space-2) 5px var(--space-3);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-pill);
  background: var(--surface-panel);
  color: var(--ink-primary);
  font-size: var(--text-sm);
  font-weight: 500;
  cursor: pointer;
  transition: background var(--dur-1) var(--ease-out), border-color var(--dur-1) var(--ease-out);

  & > svg { color: var(--ink-subtle); flex-shrink: 0; }
  &:hover { background: var(--surface-sunken); }
  &:focus-visible { outline: 2px solid var(--border-focus); outline-offset: 2px; }
}
.tmenu__label { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.tmenu__caret { opacity: 0.7; }
</style>

<!-- Unscoped: the check sits in the portalled MenuContent (Reka -> <body>), so a
     scoped hash wouldn't reach it. -->
<style>
.tmenu__check { margin-left: auto; color: var(--ink-muted); }
</style>
